// src/pages/dataInput/sources/DDLSource.tsx
import React, { useEffect, useRef, useState } from "react";
import { cls } from "../../theme/theme";
import { parseDDL, ParsedDDL, ParsedTable } from "../utils/ddlParser";
import { SourcePayload, SheetMode, Relationship } from "../types";

interface Props {
  initialFileName?: string;
  initialDDLText?:  string;
  initialSheetMode?:SheetMode;
  onChange: (payload: SourcePayload | null) => void;
}

export const DDLSource: React.FC<Props> = ({
  initialFileName = "",
  initialDDLText  = "",
  initialSheetMode = "sub_entities",
  onChange,
}) => {
  const [fileName,  setFileName]  = useState(initialFileName);
  const [ddlText,   setDDLText]   = useState(initialDDLText);
  const [parsed,    setParsed]    = useState<ParsedDDL>({ tables: [], relationships: [], warnings: [] });
  const [sheetMode, setSheetMode] = useState<SheetMode>(initialSheetMode);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Re-parse on text change
  useEffect(() => {
    if (!ddlText.trim()) {
      setParsed({ tables: [], relationships: [], warnings: [] });
      return;
    }
    setParsed(parseDDL(ddlText));
  }, [ddlText]);

  // Emit payload upward
  useEffect(() => {
    if (parsed.tables.length === 0) { onChange(null); return; }

    const availableTables = parsed.tables.map(t => ({
      prefix:  t.table_name.toUpperCase().replace(/\s+/g, "_"),
      label:   t.table_name,
      columns: t.columns.map(c => c.name),
    }));

    const ddlRels: Relationship[] = parsed.relationships.map(r => ({
      from_table:  r.from_table.toUpperCase().replace(/\s+/g, "_"),
      from_column: r.from_column,
      to_table:    r.to_table.toUpperCase().replace(/\s+/g, "_"),
      to_column:   r.to_column,
      source:      "ddl",
      reason:      "Declared as FOREIGN KEY in DDL.",
    }));

    onChange({
      kind:          "ddl",
      label:         fileName || `DDL · ${parsed.tables.length} table${parsed.tables.length === 1 ? "" : "s"}`,
      sheetMode,
      parsedTables:  parsed.tables,
      availableTables,
      relationships: ddlRels,
    });
  }, [parsed, fileName, sheetMode, onChange]);

  const onFile = async (fl: FileList | null) => {
    if (!fl || fl.length === 0) return;
    const f = fl[0];
    setFileName(f.name);
    setDDLText(await f.text());
  };

  const editTableField = (idx: number, patch: Partial<ParsedTable>) => {
    setParsed(prev => ({
      ...prev,
      tables: prev.tables.map((t, i) => i === idx ? { ...t, ...patch } : t),
    }));
  };

  const editColumn = (
    tableIdx: number,
    colIdx: number,
    patch: Partial<ParsedTable["columns"][number]>
  ) => {
    setParsed(prev => ({
      ...prev,
      tables: prev.tables.map((t, i) => {
        if (i !== tableIdx) return t;
        return {
          ...t,
          columns: t.columns.map((c, j) => j === colIdx ? { ...c, ...patch } : c),
        };
      }),
    }));
  };

  return (
    <div>
      <label
        className={cls("gw-dropzone", ddlText && "has-files")}
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); onFile(e.dataTransfer.files); }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".sql,.ddl,.txt"
          style={{ display: "none" }}
          onChange={e => onFile(e.target.files)}
        />
        {!ddlText ? (
          <>
            <div className="gw-dropzone-icon">📜</div>
            <div className="gw-dropzone-label">
              Drop a .sql / .ddl file, or paste DDL text below
            </div>
            <div className="gw-dropzone-sub">
              CREATE TABLE statements with optional PRIMARY KEY and FOREIGN KEY clauses
            </div>
          </>
        ) : (
          <div style={{ width: "100%", textAlign: "left" }}>
            <span className="gw-badge gw-badge-brand">{fileName || "(pasted)"}</span>
          </div>
        )}
      </label>

      <textarea
        className="gw-textarea"
        rows={6}
        style={{ marginTop: 12, fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}
        placeholder="-- Or paste CREATE TABLE statements here…"
        value={ddlText}
        onChange={e => { setDDLText(e.target.value); if (!fileName) setFileName(""); }}
      />

      {parsed.tables.length > 0 && (
        <>
          {/* Mode toggle */}
          {parsed.tables.length > 1 && (
            <div style={{ marginTop: 14 }}>
              <div className="gw-mode-toggle">
                {(["sub_entities", "separate_entities"] as SheetMode[]).map(m => (
                  <button
                    key={m}
                    className={cls("gw-mode-toggle-btn", sheetMode === m && "active")}
                    onClick={() => setSheetMode(m)}
                  >
                    {m === "sub_entities"
                      ? "Group as one entity"
                      : "One entity per table"}
                  </button>
                ))}
              </div>
              <div className="gw-mode-explainer">
                {sheetMode === "sub_entities"
                  ? `All ${parsed.tables.length} tables will be sub-entities under a single parent entity.`
                  : `Each of the ${parsed.tables.length} tables becomes its own independent entity.`}
              </div>
            </div>
          )}

          <div className="gw-ddl-preview">
            <div className="gw-ddl-preview-hdr">
              <span>
                Parsed: <strong>{parsed.tables.length}</strong> table
                {parsed.tables.length === 1 ? "" : "s"}
                {parsed.relationships.length > 0 && (
                  <> · <strong>{parsed.relationships.length}</strong> relationship
                    {parsed.relationships.length === 1 ? "" : "s"}</>
                )}
              </span>
              <span style={{ fontSize: 11, color: "#445566" }}>Edit columns inline if anything looks wrong</span>
            </div>

            {parsed.tables.map((t, ti) => (
              <div className="gw-ddl-table" key={ti}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                  <input
                    className="gw-sheet-field-edit"
                    style={{ maxWidth: 220, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}
                    value={t.table_name}
                    onChange={e => editTableField(ti, { table_name: e.target.value })}
                  />
                  <input
                    className="gw-sheet-field-edit"
                    style={{ flex: 1 }}
                    placeholder="Table description (optional)"
                    value={t.description}
                    onChange={e => editTableField(ti, { description: e.target.value })}
                  />
                </div>
                <div className="gw-ddl-cols">
                  {t.columns.map((c, ci) => (
                    <div className="gw-ddl-col-row" key={ci}>
                      <span className="gw-ddl-col-pk">{c.is_pk ? "🔑" : ""}</span>
                      <input
                        className="gw-sheet-field-edit"
                        style={{ maxWidth: 180, fontFamily: "'JetBrains Mono', monospace" }}
                        value={c.name}
                        onChange={e => editColumn(ti, ci, { name: e.target.value })}
                      />
                      <input
                        className="gw-sheet-field-edit"
                        style={{ maxWidth: 130, fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}
                        value={c.data_type}
                        onChange={e => editColumn(ti, ci, { data_type: e.target.value })}
                      />
                      <input
                        className="gw-sheet-field-edit"
                        style={{ flex: 1, fontSize: 11 }}
                        placeholder="description (optional)"
                        value={c.description}
                        onChange={e => editColumn(ti, ci, { description: e.target.value })}
                      />
                      <span className="gw-ddl-col-null">{c.nullable ? "NULL" : "NOT NULL"}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {parsed.warnings.length > 0 && (
              <div className="gw-ddl-warn">
                <strong>{parsed.warnings.length} parser warning{parsed.warnings.length === 1 ? "" : "s"}:</strong>
                <ul style={{ marginTop: 4, paddingLeft: 18 }}>
                  {parsed.warnings.slice(0, 5).map((w, i) => <li key={i}>{w}</li>)}
                  {parsed.warnings.length > 5 && <li>…and {parsed.warnings.length - 5} more</li>}
                </ul>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DDLSource;